
import React from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  User, Mail, Phone, Calendar, ShoppingBag, 
  Check, X, Edit, Trash2, AlertTriangle, 
  Shield, Lock, Unlock, ShieldOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface UserDetailsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: any;
  products: any[];
  loadingProducts: boolean;
  checkAdminStatus: (userId: string) => Promise<boolean>;
  openEditUser: (user: any) => void;
  deleteUserProduct: (userId: string, productId: string) => void;
  confirmDeleteUser: (userId: string) => void;
}

export const UserDetailsDialog: React.FC<UserDetailsDialogProps> = ({
  open,
  setOpen,
  user,
  products,
  loadingProducts,
  checkAdminStatus,
  openEditUser,
  deleteUserProduct,
  confirmDeleteUser
}) => {
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  
  React.useEffect(() => {
    const loadAdminStatus = async () => {
      if (user?.id) {
        const status = await checkAdminStatus(user.id);
        setIsAdmin(status);
      }
    };
    
    if (open && user?.id) {
      loadAdminStatus();
    }
  }, [open, user, checkAdminStatus]);

  if (!user) return null;

  const userInitials = user.full_name 
    ? user.full_name.split(' ').map((name: string) => name[0]).join('').toUpperCase()
    : user.email ? user.email[0].toUpperCase() : '?';

  const formattedCreatedAt = user.created_at 
    ? new Date(user.created_at).toLocaleDateString()
    : 'Non disponible';

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'verified':
        return "green";
      case 'blocked':
        return "red";
      case 'admin':
        return "blue";
      default:
        return "secondary";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Détails de l'utilisateur
          </DialogTitle>
          <DialogDescription>
            Informations complètes sur le profil utilisateur et ses produits associés.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-20 w-20 border-2 border-primary/20 rounded-full overflow-hidden">
              <Avatar className="h-full w-full">
                <AvatarImage src={user.avatar_url} alt={user.full_name || "Utilisateur"} />
                <AvatarFallback>{userInitials}</AvatarFallback>
              </Avatar>
            </div>
            
            <div className="flex-1 space-y-1.5">
              <h3 className="text-lg font-semibold">
                {user.full_name || 'Non renseigné'}
              </h3>
              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" /> {user.email}
                </span>
                {user.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" /> {user.phone}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant={getStatusVariant('admin')} className={isAdmin ? "opacity-100" : "opacity-0"}>
                  Administrateur
                </Badge>
                <Badge variant={getStatusVariant('verified')} className={user.verified ? "opacity-100" : "opacity-0"}>
                  Vérifié
                </Badge>
                <Badge variant={getStatusVariant('blocked')} className={user.blocked ? "opacity-100" : "opacity-0"}>
                  Bloqué
                </Badge>
                <Badge variant="secondary">
                  {user.type === 'professional' ? 'Professionnel' : 'Utilisateur'}
                </Badge>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 sm:self-start">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1.5"
                onClick={() => openEditUser(user)}
              >
                <Edit className="h-3.5 w-3.5" />
                Modifier
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                className="gap-1.5"
                onClick={() => confirmDeleteUser(user.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </Button>
            </div>
          </div>
          
          <Separator />
          
          <Tabs defaultValue="profile">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="profile">Profil</TabsTrigger>
              <TabsTrigger value="products">Produits</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="space-y-4 mt-4">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="font-medium">Informations personnelles</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Nom complet</p>
                            <p className="text-sm text-muted-foreground">{user.full_name || 'Non renseigné'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Email</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Téléphone</p>
                            <p className="text-sm text-muted-foreground">{user.phone || 'Non renseigné'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="font-medium">Informations de compte</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Date d'inscription</p>
                            <p className="text-sm text-muted-foreground">{formattedCreatedAt}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <ShoppingBag className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Type de compte</p>
                            <p className="text-sm text-muted-foreground capitalize">{user.type || 'Standard'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          {isAdmin ? (
                            <Shield className="h-4 w-4 text-blue-500 mt-0.5" />
                          ) : (
                            <ShieldOff className="h-4 w-4 text-muted-foreground mt-0.5" />
                          )}
                          <div>
                            <p className="text-sm font-medium">Statut administrateur</p>
                            <p className="text-sm text-muted-foreground">
                              {isAdmin ? 'Administrateur' : 'Utilisateur standard'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          {user.verified ? (
                            <Check className="h-4 w-4 text-green-500 mt-0.5" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground mt-0.5" />
                          )}
                          <div>
                            <p className="text-sm font-medium">Vérification</p>
                            <p className="text-sm text-muted-foreground">
                              {user.verified ? 'Compte vérifié' : 'Non vérifié'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          {user.blocked ? (
                            <Lock className="h-4 w-4 text-red-500 mt-0.5" />
                          ) : (
                            <Unlock className="h-4 w-4 text-muted-foreground mt-0.5" />
                          )}
                          <div>
                            <p className="text-sm font-medium">Statut du compte</p>
                            <p className="text-sm text-muted-foreground">
                              {user.blocked ? 'Compte bloqué' : 'Compte actif'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="products" className="space-y-4 mt-4">
              {loadingProducts ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : products && products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium truncate">{product.name}</h4>
                              <p className="text-sm text-muted-foreground truncate">
                                {product.price ? `${product.price} €` : 'Prix non défini'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-8 w-8 p-0"
                              onClick={() => deleteUserProduct(user.id, product.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="mt-2">
                            <p className="text-sm line-clamp-2">
                              {product.description || 'Aucune description'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-lg font-medium">Aucun produit</h3>
                  <p className="text-sm text-muted-foreground">
                    Cet utilisateur n'a pas encore de produits associés.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Fermer</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
